import type { IddProduktTraining, IddSzenario } from "./idd"

/**
 * The creditable modules. A conversation module is one training unit; its key
 * is the unit's key, so the yearly rule reads "each unit once a year", with
 * whatever customer it was trained. Changing one in substance means bumping
 * IDD_KATALOG_FASSUNG, so certificates keep naming the rules they were earned under.
 */
export const IDD_SZENARIEN: IddSzenario[] = [
  {
    key: "terminierung",
    titel: "Terminvereinbarung am Telefon",
    kurz: "Anrufen und einen Beratungstermin vereinbaren.",
    beschreibung:
      "Du rufst jemanden aus dem Bestand an und vereinbarst einen Beratungstermin. Am Telefon, ohne Bild.",
    vorlagePersonaId: "christina",
    einheit: "terminierung",
    minuten: 20,
    auftrag:
      "Stell dich vor, nenne den Anlass, mach den Nutzen greifbar, nimm Einwände auf und vereinbare einen konkreten Termin.",
    meilensteine: [
      {
        key: "vorstellung",
        titel: "Begrüßung und Vorstellung",
        kriterium:
          "Die Beraterin begrüßt den Kunden und nennt ihren Namen und die Sparkasse.",
      },
      {
        key: "anlass",
        titel: "Anlass des Anrufs",
        kriterium:
          "Die Beraterin nennt einen konkreten Anlass für den Anruf, etwa das Einkommensgutachten als kostenfreien Service.",
      },
      {
        key: "nutzen",
        titel: "Nutzen für den Kunden",
        kriterium:
          "Die Beraterin macht verständlich, was der Kunde persönlich von dem Termin hat.",
      },
      {
        key: "einwand",
        titel: "Einwand aufgenommen",
        kriterium:
          "Die Beraterin nimmt einen Einwand des Kunden auf, zeigt Verständnis oder fragt nach, statt ihn zu übergehen.",
      },
      {
        key: "termin",
        titel: "Konkreter Termin",
        kriterium:
          "Ein Termin mit Tag und Uhrzeit ist vereinbart und vom Kunden bestätigt.",
      },
      {
        key: "unterlagen",
        titel: "Unterlagen angesprochen",
        kriterium:
          "Die Beraterin bittet den Kunden, Unterlagen mitzubringen, etwa die Renteninformation.",
      },
    ],
  },
  {
    key: "rahmen_bedarf",
    titel: "Altersvorsorgegespräch",
    kurz: "Bedarf erheben und die Versorgungslücke gemeinsam herleiten.",
    beschreibung:
      "Du führst durch Gesprächsrahmen und Bedarfsanalyse bis zur Versorgungslücke und vereinbarst den nächsten Schritt.",
    vorlagePersonaId: "tobias",
    einheit: "rahmen_bedarf",
    minuten: 45,
    auftrag:
      "Steck den Rahmen ab, erhebe die Situation, leite die Versorgungslücke mit dem Kunden her und kläre Risikobereitschaft und nächsten Schritt.",
    meilensteine: [
      {
        key: "rahmen",
        titel: "Gesprächsrahmen",
        kriterium:
          "Die Beraterin klärt Ziel, Ablauf oder Dauer des Gesprächs und holt das Einverständnis des Kunden ein.",
      },
      {
        key: "situation",
        titel: "Ist-Situation erhoben",
        kriterium:
          "Die Beraterin fragt nach Einkommen, Familie oder bestehender Vorsorge des Kunden.",
      },
      {
        key: "wunsch",
        titel: "Ziele im Alter",
        kriterium:
          "Die Beraterin fragt, welches Einkommen oder welchen Lebensstandard sich der Kunde im Ruhestand wünscht.",
      },
      {
        key: "luecke",
        titel: "Versorgungslücke hergeleitet",
        kriterium:
          "Die Beraterin stellt gesetzliche Rente und Wunsch gegenüber, nennt die Lücke und lässt sie vom Kunden bestätigen.",
      },
      {
        key: "risiko",
        titel: "Risikobereitschaft und Anlagehorizont",
        kriterium:
          "Die Beraterin fragt nach Risikobereitschaft, Verlusttragfähigkeit oder Anlagehorizont des Kunden.",
      },
      {
        key: "naechster_schritt",
        titel: "Nächster Schritt vereinbart",
        kriterium:
          "Die Beraterin vereinbart mit dem Kunden einen konkreten nächsten Schritt, etwa einen Folgetermin für die Lösung.",
      },
    ],
  },
  {
    key: "loesung_abschluss",
    titel: "Meilensteinbasierte Gesprächsführung",
    kurz: "Von der Bedarfszusammenfassung bis zur Abschlussfrage.",
    beschreibung:
      "Folgetermin mit bekanntem Bedarf. Du führst Meilenstein für Meilenstein durch Lösung, Kosten, Einwand und Abschlussfrage.",
    vorlagePersonaId: "anke",
    einheit: "loesung_abschluss",
    minuten: 45,
    auftrag:
      "Fasse den Bedarf zusammen, präsentiere eine passende Lösung, mach Kosten und Risiken transparent, behandle den Einwand und stell die Abschlussfrage.",
    meilensteine: [
      {
        key: "bedarf_zusammenfassen",
        titel: "Bedarf zusammengefasst",
        kriterium:
          "Die Beraterin fasst den bekannten Bedarf des Kunden zusammen und lässt ihn sich bestätigen.",
      },
      {
        key: "loesung",
        titel: "Lösung am Bedarf ausgerichtet",
        kriterium:
          "Die Beraterin stellt eine Lösung vor und begründet sie mit dem Bedarf des Kunden, nicht mit Produktmerkmalen allein.",
      },
      {
        key: "kosten_risiken",
        titel: "Kosten und Risiken transparent",
        kriterium:
          "Die Beraterin spricht Kosten, Garantien oder Risiken der Lösung offen an.",
      },
      {
        key: "einwand",
        titel: "Einwand behandelt",
        kriterium:
          "Die Beraterin nimmt einen Einwand auf, fragt nach dem Hintergrund und beantwortet ihn, ohne Druck aufzubauen.",
      },
      {
        key: "abschlussfrage",
        titel: "Abschlussfrage gestellt",
        kriterium:
          "Die Beraterin fragt den Kunden klar nach seiner Entscheidung oder schlägt den konkreten Abschluss vor.",
      },
      {
        key: "nachbetreuung",
        titel: "Nachbetreuung vereinbart",
        kriterium:
          "Die Beraterin vereinbart, wie es weitergeht, etwa Unterlagen, Bedenkzeit mit festem Termin oder regelmäßige Überprüfung.",
      },
    ],
  },
]

export const IDD_PRODUKT_TRAININGS: IddProduktTraining[] = [
  {
    key: "aktivplan",
    produkt: "aktivplan",
    titel: "neue leben Aktivplan",
    beschreibung:
      "Die private Rentenversicherung im Zielsortiment, in sechs Lernbausteinen. Du wählst, welche du bearbeitest.",
    mindestKapitel: 3,
    kapitel: [
      {
        key: "zielmarkt",
        titel: "Zielmarkt",
        beschreibung:
          "Für wen der Aktivplan gedacht ist, welche Bedürfnisse er deckt und wo er nicht passt.",
        minuten: 15,
        einstieg:
          "Fangen wir beim Zielmarkt an. Was meinst du: Für welche Kundinnen und Kunden ist der Aktivplan gedacht, und für wen ausdrücklich nicht?",
      },
      {
        key: "garantien",
        titel: "Garantien",
        beschreibung:
          "Garantiertes Mindestkapital, Mindestrente, Rückkaufswerte und was davon wirklich garantiert ist.",
        minuten: 15,
        einstieg:
          "Weiter mit den Garantien. Ein Kunde fragt dich: „Was ist bei dem Vertrag eigentlich garantiert?“ Was antwortest du?",
      },
      {
        key: "leistungskomponenten",
        titel: "Leistungskomponenten",
        beschreibung:
          "Konventioneller und fondsgebundener Anteil, Leistung im Erlebens- und im Todesfall.",
        minuten: 15,
        einstieg:
          "Jetzt zu den Leistungskomponenten. Wie würdest du einem Kunden in zwei Sätzen erklären, warum der Aktivplan eine Hybridrente ist?",
      },
      {
        key: "auszahlungsoptionen",
        titel: "Auszahlungsoptionen",
        beschreibung:
          "Rente oder Kapital, die drei Formen des Rentenbezugs, Rentengarantiezeit und Entnahmen im Rentenbezug.",
        minuten: 15,
        einstieg:
          "Kommen wir zur Auszahlung. Welche Möglichkeiten hat eine Kundin, wenn ihr Aktivplan zum Rentenbeginn fällig wird?",
      },
      {
        key: "pflegeleistungen",
        titel: "Pflegeleistungen",
        beschreibung:
          "Die Pflegekomponente: Voraussetzungen, Pflegegrade und was sie kostet.",
        minuten: 15,
        einstieg:
          "Nächstes Kapitel: Pflege. Was weißt du schon über die Pflegekomponente im Aktivplan?",
      },
      {
        key: "flexibilitaet",
        titel: "Zuzahlungen und Entnahmen",
        beschreibung:
          "Flexibilität in der Ansparphase: Zuzahlungen, Entnahmen, Beitragserhöhungen und Umschichtungen.",
        minuten: 15,
        einstieg:
          "Zum Schluss die Flexibilität. Ein Kunde bekommt eine Erbschaft und möchte Geld in seinen Aktivplan einzahlen. Was geht, und was musst du beachten?",
      },
    ],
  },
]

